<template>
  <hr-page>
    <breadcrumbs />
    <div class="q-pa-none">
      <records-table
        v-model:pagination="pagination"
        :title="$t(`settings.${ isFormTemplates ? 'formTemplates' : 'noteTemplates' }.breadcrumbElLabel`)"
        standalone
        :rows="sessionNoteTemplates"
        :row-name="(row) => row.noteTemplateName"
        :columns="columns"
        :loading="loadingTemplates"
        :no-data-label="$t('settings.templates.tableNoDataLabel')"
        :search-placeholder="$t(`settings.${ isFormTemplates ? 'formTemplates' : 'noteTemplates' }.inputSearch`)"
        :description="$t(`settings.${ isFormTemplates ? 'formTemplates' : 'noteTemplates' }.textCaption`)"
        disable-export
        :create="createAction"
        :configure="configureAction"
        row-key="id"
        @row-click="onEditNoteTemplateRow"
      >
        <template #row-actions="{ row, actionsLabel }">
          <hr-overflow-menu-btn
            :aria-label="actionsLabel"
            :options="[
              { group: 'lifecycle', options: [
                { label: $t('settings.listItemTextEdit'), icon: 'edit',
                  disable: !networkOnline || offlineMode, action: () => onEditNoteTemplateRow(null, row) },
                { label: $t('programs.listItemDuplicate'), icon: 'copy_all',
                  disable: offlineMode, action: () => onDuplicateNoteTemplate(row) },
              ]},
              { group: 'destructive', options: [
                { label: $t('settings.listItemTextDelete'), icon: 'delete',
                  destructive: true, disable: !networkOnline || offlineMode,
                  action: () => onDeleteNoteTemplate(row.id) },
              ]},
              ...(userIsAdmin ? [{ group: 'admin', options: [
                { label: 'Copy to Clinic', icon: 'verified_user',
                  action: () => onCopyNoteTemplateToClinic(row) },
              ]}] : []),
            ]"
          />
        </template>

        <template #empty-filtered="{ clearSearch }">
          <hr-empty-state
            icon="search_off"
            :title="$t(`settings.${ isFormTemplates ? 'formTemplates' : 'noteTemplates' }.emptyFilteredTitle`)"
            :message="$t('settings.templates.emptyFilteredMessage')"
          >
            <template #actions>
              <hr-supporting-btn
                :label="$t('common.buttonClearSearch')"
                data-e2e="button-clear-search"
                @click="clearSearch"
              />
            </template>
          </hr-empty-state>
        </template>
      </records-table>
    </div>
  </hr-page>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import SelectClinic from 'components/Admin/SelectClinic';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import EditSessionNotesLayout from 'components/Sessions/SessionNoteTemplates/EditSessionNotesLayout.vue';
import EditSessionNoteTemplate from 'components/Sessions/SessionNoteTemplates/EditSessionNoteTemplate';
import { isEmpty, map, omit } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import RecordsTable from 'components/UI/RecordsTable.vue';
import {
  confirmDestructiveDialog, confirmDialog, HrEmptyState, HrOverflowMenuBtn, HrPage, HrSupportingBtn,
} from 'src/design-system';
import { isAuditorEnabledForTemplate } from 'src/domains/noteAuditor';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionNoteTemplatesDB from 'src/fb/sessionNoteTemplates-db';
import SessionsDB from 'src/fb/sessions-db';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { network } from 'src/store/network';
import { careModelAnalytics } from 'src/services/analytics';
import { logEvent } from 'src/utils/LogEvent';

export default {

  name: 'NoteTemplates',
  components: {
    Breadcrumbs,
    HrEmptyState,
    HrOverflowMenuBtn,
    HrPage,
    HrSupportingBtn,
    RecordsTable,
  },
  mixins: [Interactions],

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.loadNoteTemplates();
      vm.initNoteTemplatesDefaultLayout();
    });
  },

  data() {
    return {
      loadingTemplates: false,
      loadingLayoutSettings: false,

      sessionNoteTemplates: [],

      pagination: {
        sortBy: 'noteTemplateName',
        descending: false,
        page: 1,
        rowsPerPage: 25,
      },

      columns: [
        {
          name: 'noteTemplateName',
          required: true,
          label: this.$t('settings.templates.columnsNoteTemplateNameLabel'),
          align: 'left',
          field: 'noteTemplateName',
          sortable: true,
        },
      ],

      layoutSettings: {
        showPageHeader: false,
        pageHeaderContent: '',
        showPageFooter: false,
        pageFooterContent: '',
      },

      // Remember each dialog's maximized size for the page session (reset on reload) — the
      // dialogs are remounted on every open, so the memory must live here on the page.
      noteTemplateDialogMaximized: false,
      layoutDialogMaximized: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'sessionNotesLayoutSettings', 'userIsAdmin']),
    ...mapState(network, ['networkOnline', 'offlineMode']),
    ...mapState(clients, ['clients']),

    createAction() {
      return {
        key: 'add-note-template',
        label: this.$t(`settings.${this.isFormTemplates ? 'formTemplates' : 'noteTemplates'}.buttonAddNoteTemplate`),
        dataE2e: 'button-add-note-template',
        disable: this.offlineMode,
        handler: this.onAddNoteTemplate,
      };
    },

    /*
     * The header and footer every template is rendered with, which is the shape of the notes made
     * from here on rather than anything about the templates listed. That is what the configure
     * control is for, so it stops being a second page button beside the one that creates.
     */
    configureAction() {
      return {
        key: 'layout-settings',
        label: this.$t('settings.templates.buttonLayoutSettings'),
        disable: this.offlineMode,
        loading: this.loadingLayoutSettings,
        handler: this.onOpenLayoutSettings,
      };
    },

    isFormTemplates() {
      return !!this.$attrs.isFormTemplates;
    },
  },

  methods: {
    ...mapActions(auth, ['loadClinicSettings']),

    onAddNoteTemplate() {
      this.openComponentDialog(
        EditSessionNoteTemplate,
        {
          sessionNoteTemplate: {},
          isFormTemplate: this.isFormTemplates,
          maximized: this.noteTemplateDialogMaximized,
          'onUpdate:maximized': (value) => { this.noteTemplateDialogMaximized = value; },
        },
        async (savedTemplate) => {
          await this.loadNoteTemplates();
          this.reportAuditorToggle(savedTemplate, false);
        },
      );
    }, // onAddNoteTemplate()

    async onEditNoteTemplateRow(_, sessionNoteTemplate) {
      if (!sessionNoteTemplate) {
        return;
      }

      this.openComponentDialog(
        EditSessionNoteTemplate,
        {
          sessionNoteTemplate,
          isFormTemplate: this.isFormTemplates,
          maximized: this.noteTemplateDialogMaximized,
          'onUpdate:maximized': (value) => { this.noteTemplateDialogMaximized = value; },
        },
        async (savedTemplate) => {
          await this.loadNoteTemplates();
          this.reportAuditorToggle(savedTemplate, isAuditorEnabledForTemplate(sessionNoteTemplate));
        },
      );
    }, // onEditNoteTemplate()

    /**
     * @param {{ sessionNoteTemplateId: string, enableNoteAuditor: boolean }} savedTemplate
     * @param {boolean} wasEnabledBefore
     */
    reportAuditorToggle(savedTemplate, wasEnabledBefore) {
      if (!savedTemplate || savedTemplate.enableNoteAuditor === wasEnabledBefore) {
        return;
      }

      careModelAnalytics.trackNoteTemplateAuditorToggled({
        sessionNoteTemplateId: savedTemplate.sessionNoteTemplateId,
        enabled: savedTemplate.enableNoteAuditor,
        templatesWithAuditor: this.sessionNoteTemplates
          .filter((template) => isAuditorEnabledForTemplate(template)).length,
        templatesTotal: this.sessionNoteTemplates.length,
      });
    }, // reportAuditorToggle()

    async onDeleteNoteTemplate(noteTemplateId) {
      let isInUse;

      this.loadingTemplates = true;
      try {
        isInUse = await this.isNoteTemplateInUse(noteTemplateId);
      } catch (err) {
        this.notifyNegative(err.message);

        return;
      } finally {
        this.loadingTemplates = false;
      }

      const message = isInUse ? 'dialogDeleteNoteTemplateInUseMessage' : 'dialogDeleteTemplateMessage';

      confirmDestructiveDialog(
        this.$t('settings.templates.dialogDeleteTemplateTitle'),
        this.$t(`settings.templates.${message}`),
        async () => {
          this.loadingTemplates = true;
          try {
            const [sessionsIds, sessionActivitiesIds] = await Promise.all([
              this.getSessionsWithNoteTemplate(noteTemplateId),
              this.getSessionActivitiesWithNoteTemplate(noteTemplateId),
            ]);

            await this.deleteNoteTemplate(noteTemplateId);

            if (sessionsIds.length > 0) {
              await this.deleteTemplateFromSessions(sessionsIds);
            }

            if (sessionActivitiesIds.length > 0) {
              await this.deleteTemplateFromSessionActivities(sessionActivitiesIds);
            }
          } catch (err) {
            this.notifyNegative(err.message);
          } finally {
            this.loadingTemplates = false;
          }
        },
        { confirmLabel: this.$t('common.buttonDelete') },
      );
    }, // onDeleteNoteTemplate()

    onDuplicateNoteTemplate(template) {
      confirmDialog(
        this.$t('settings.templates.dialogDuplicateTemplateTitle'),
        this.$t('settings.templates.dialogDuplicateNoteTemplateMessage'),
        async () => {
          try {
            const templateCopy = omit(template, 'id');
            templateCopy.noteTemplateName = `${template.noteTemplateName} (${this.$t('common.buttonCopy')})`;
            if (templateCopy.fields?.length) {
              // Generate new IDs for each field and store the mapping
              const newIdByOldId = templateCopy.fields.reduce((acc, field) => {
                acc[field.id] = uid();
                return acc;
              }, {});

              // Replace old field IDs with new ones
              templateCopy.fields = templateCopy.fields.map((field) => ({
                ...field,
                id: newIdByOldId[field.id],
              }));

              // Replace field IDs inside the templateText html string
              Object.entries(newIdByOldId).forEach(([oldId, newId]) => {
                templateCopy.templateText = templateCopy.templateText.replaceAll(oldId, newId);
              });
            }
            const sessionNoteTemplateDb = new SessionNoteTemplatesDB();
            const newTemplate = await sessionNoteTemplateDb.create(templateCopy);
            this.sessionNoteTemplates.push(newTemplate);
            /*
             * A copy inherits the flag, so the clinic gains an auditor-enabled template without the
             * checkbox ever being touched. Reported here rather than from the editor below, which
             * opens with the inherited value as its baseline and would see no change — and which the
             * user may close without saving at all.
             */
            this.reportAuditorToggle(
              {
                sessionNoteTemplateId: newTemplate.id,
                enableNoteAuditor: isAuditorEnabledForTemplate(newTemplate),
              },
              false,
            );
            this.onEditNoteTemplateRow(null, newTemplate);
            this.notifySuccess(this.$t('settings.templates.notifyDuplicateNoteTemplateSuccess'));
          } catch (error) {
            console.error('Duplicate note template error', error);
            logEvent(error, {
              context: {
                duplicate_action: {
                  id: template.id,
                  template,
                },
              },
            });
          }
        },
        { confirmLabel: this.$t('common.buttonDuplicate') },
      );
    }, // onDuplicateNoteTemplate()

    onCopyNoteTemplateToClinic(template) {
      // Select clinic popup.
      // This is an admin function and therefore not translated
      this.openComponentDialog(
        SelectClinic,
        {
          title: 'Copy to Clinic',
          text: 'Select the destination clinic to copy this session note template to.',
          okButton: this.$t('common.buttonCopy'),
        },
        async (selectedClinic) => {
          try {
            this.$q.loading.show();
            await callFirebaseFunction('copyNoteTemplate', {
              sourceClinicId: this.userDetails.clinic,
              destRegion: selectedClinic.region,
              destClinicId: selectedClinic.clinicId,
              noteTemplateId: template.id,
            });

            this.notifySuccess('Session note template successfully copied...');
          } catch (err) {
            this.notifyNegative(err.message);
          } finally {
            this.$q.loading.hide();
          }
        },
      );
    }, // onCopyNoteTemplateToClinic()

    onOpenLayoutSettings() {
      this.openComponentDialog(
        EditSessionNotesLayout,
        {
          layoutSettings: this.layoutSettings,
          maximized: this.layoutDialogMaximized,
          'onUpdate:maximized': (value) => { this.layoutDialogMaximized = value; },
        },
        async () => {
          this.initNoteTemplatesDefaultLayout();
        },
      );
    }, // onOpenLayoutSettings()

    /**
     * Whether anything still points at the template, read one document deep: the dialog only needs
     * to know which message to show, and the ids to clear are read once the delete is confirmed.
     *
     * Unlike the full read, it does not skip deleted session activities or those of clients the
     * reader cannot see, so it can warn about a template the delete then clears from nothing. That is
     * the safe way to be wrong; the reverse would delete without the warning.
     *
     * @param {string} templateId
     * @returns {Promise<boolean>}
     */
    async isNoteTemplateInUse(templateId) {
      const [sessions, sessionActivities] = await Promise.all([
        new SessionsDB().readAll(
          [['clinic', '==', this.userDetails.clinic], ['sessionNoteTemplate', '==', templateId]],
          null,
          null,
          1,
        ),
        new SessionActivitiesDB().readAll(
          [['clinic', '==', this.userDetails.clinic], ['sessionNoteTemplateId', '==', templateId]],
          null,
          null,
          1,
        ),
      ]);

      return sessions.length > 0 || sessionActivities.length > 0;
    }, // isNoteTemplateInUse()

    /**
     * Find sessions that use a Note Template the user wants to delete
     *
     * @param {string} templateId
     * @returns {Promise<string[]>} - array of sessions ids
     */
    async getSessionsWithNoteTemplate(templateId) {
      try {
        const sessionDb = new SessionsDB();
        const sessions = await sessionDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
            ['sessionNoteTemplate', '==', templateId],
          ],
        );

        return map(sessions, 'id');
      } catch (err) {
        logEvent(err);
        throw new Error(err);
      }
    }, // getSessionsWithNoteTemplate()

    /**
     * Find session activities that use a Note Template the user wants to delete
     *
     * @param {string} templateId
     * @returns {Promise<string[]>} - array of session activities ids
     */
    async getSessionActivitiesWithNoteTemplate(templateId) {
      try {
        const sessionActivitiesDb = new SessionActivitiesDB();
        const sessionActivities = await sessionActivitiesDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
            ['sessionNoteTemplateId', '==', templateId],
          ],
        );

        const existingClients = this.clients.map((client) => (client.id));

        const filteredSessionActivities = sessionActivities.filter((sessionActivity) => (
          !sessionActivity.isDeleted
          && (!sessionActivity.client || existingClients.includes(sessionActivity.client))
        ));

        return map(filteredSessionActivities, 'id');
      } catch (err) {
        logEvent(err);
        throw new Error(err);
      }
    }, // getSessionActivitiesWithNoteTemplate()

    /**
     * Set Note Template of selected sessions to null
     *
     * @param {Array.<string>} sessionsIds - array of sessions ids
     */
    async deleteTemplateFromSessions(sessionsIds) {
      try {
        const sessionsDb = new SessionsDB();
        await Promise.all(map(sessionsIds, async (sessionId) => sessionsDb.update({
          id: sessionId,
          sessionNoteTemplate: null,
        })));
      } catch (err) {
        logEvent(err);
        this.notifyNegative(err);
      }
    }, // deleteTemplateFromSessions()

    /**
     * Set Note Template of selected session activities to null
     *
     * @param {Array.<string>} sessionActivitiesIds - array of session activities ids
     */
    async deleteTemplateFromSessionActivities(sessionActivitiesIds) {
      try {
        const sessionActivitiesDb = new SessionActivitiesDB();
        await Promise.all(map(sessionActivitiesIds, async (sessionActivityId) => sessionActivitiesDb.update({
          id: sessionActivityId,
          sessionNoteTemplateId: null,
        })));
      } catch (err) {
        logEvent(err);
        this.notifyNegative(err);
      }
    }, // deleteTemplateFromSessionActivities()

    /**
     * Mark a note template as deleted. Templates are permanently deleted
     */
    deleteNoteTemplate(sessionNoteTemplateId) {
      const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
      sessionNoteTemplatesDb.delete(sessionNoteTemplateId);
      this.sessionNoteTemplates = this.sessionNoteTemplates.filter(
        (sessionNoteTemplate) => sessionNoteTemplate.id !== sessionNoteTemplateId,
      );
    }, // deleteNoteTemplate()

    async loadNoteTemplates() {
      this.loadingTemplates = true;
      try {
        const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
        const constraints = [
          ['clinic', '==', this.userDetails.clinic],
        ];

        if (this.isFormTemplates) {
          constraints.push(['sessionActivityTypes', 'array-contains', 'form']);
        }

        const loadedTemplates = await sessionNoteTemplatesDb.readAll(constraints);

        if (this.isFormTemplates) {
          this.sessionNoteTemplates = loadedTemplates;
        } else {
          this.sessionNoteTemplates = loadedTemplates.filter((template) => {
            return !template.sessionActivityTypes?.includes('form');
          });
        }
      } finally {
        this.loadingTemplates = false;
      }
    }, // loadNoteTemplates()

    async initNoteTemplatesDefaultLayout() {
      this.loadingLayoutSettings = true;
      try {
        await this.loadClinicSettings();

        if (!isEmpty(this.sessionNotesLayoutSettings)) {
          this.layoutSettings = this.sessionNotesLayoutSettings;
        }
      } finally {
        this.loadingLayoutSettings = false;
      }
    },

  }, // methods

};
</script>
